import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { ENDPOINTS, type VerificationPolicyResponse, type VerificationSettingsResponse } from '@clm/shared-types';

import { fetchVerificationPolicy, fetchVerificationSettings } from '../api/verification-policy';
import { mayAttempt } from '../auth/permissions';
import { useIdentity } from '../auth/useIdentity';
import { requireSurface } from '../routes/surfaces';
import { PanelProblem } from './quality/panel';
import { toProblem, type Panel } from './quality/panel-state';
import { ContractTypeAutoApprovalCard } from './verification-policy/ContractTypeAutoApprovalCard';
import { FieldPolicyTable } from './verification-policy/FieldPolicyTable';
import { SettingsCard, type LegalView } from './verification-policy/SettingsCard';
import {
  POLICY_ADMIN_ROLES,
  POLICY_LEGAL_ROLES,
  SCOPE_ALL_REQUIRED_COPY,
  VERIFICATION_POLICY_FORBIDDEN_COPY,
} from './verification-policy/shared';

/**
 * Admin **Auto-approval** — CLM-108 (FR-31, FR-32, NFR-16, CR-06; D-2.1-1 …
 * D-2.1-7, decided 1 Oct 2026; R-108-3, R-108-10 … R-108-12, R-108-17,
 * R-108-20, R-108-24).
 *
 * Per field, the organisation-wide accuracy and what the policy does with it:
 * whether a value is settled by policy or goes to a person, and why. Two reads,
 * one per role, and five writes, every one gated on the API side:
 *
 *  - `GET /v1/admin/verification-policy` (`admin:configure`) — the settings
 *    card and the per-field table. The table is the org-wide figure (D-107-1),
 *    so a caller who is not Scope-All gets `fields: null` with
 *    `fields_withheld`, and this page shows the notice in its place — never a
 *    figure counted over contracts outside their scope (R-108-12).
 *  - `GET /v1/admin/verification-policy/settings`
 *    (`compliance:cosign_verification_settings`, Legal only) — the same card,
 *    whether the caller proposed the pending change, and, for a Scope-All
 *    caller, the impact and the opt-ins awaiting co-sign.
 *
 * ## Which read is asked, and what a 403 means here
 *
 * A read is asked only of a sign-in that may hold its action
 * (`mayAttempt`, the CLM-111 precedent) — and of every sign-in whose roles the
 * SPA cannot name (D-82-1), which in production is most of them. So a 403 on
 * one read is the expected answer for a caller with the other role: that half
 * of the page is simply absent. Only when **neither** read answers is the 403
 * copy shown. The rail entry (`admin_or_legal`) is usability only (NFR-06).
 *
 * ## Nothing here decides a field
 *
 * The pipeline's own state statement, read inside the store transaction, is
 * the only read that decides whether a value is auto-approved (R-108-2). The
 * page shows the policy's figures and changes its configuration; every figure
 * is the API's, and every write re-reads both views. The last answers stay on
 * screen while a re-read is in flight, so a receipt or a refusal is not
 * unmounted by the very reload it asked for (the Base MSA page's rule).
 *
 * ## The contract type (CLM-121)
 *
 * Between the settings card and the field table, the **Contract type** card:
 * the type's own auto-approval switch (D-110-4), an Admin's alone with fixed
 * figures and no co-sign. Its own read and write; rendered only for a sign-in
 * that may hold `admin:configure`, and a 403 renders nothing.
 *
 * ## Extraction accuracy stays a measurement
 *
 * CLM-107's page keeps no threshold of its own and links here once (R-108-20).
 */

const POLICY_PATH = ENDPOINTS['verification-policy'].path;
const SETTINGS_PATH = ENDPOINTS['verification-settings'].path;
const PANEL = 'Auto-approval';

/** A read this sign-in was not asked for — its role is known and is not the one the read needs. */
type View<T> = Panel<T> | { readonly kind: 'not_asked' };

/**
 * `usePanel`, with a switch: a read the role hint rules out is never sent. One
 * `AbortController` per load, keyed on a reload nonce the page shares between
 * both reads, so one write re-reads both. The last answer is kept while a
 * re-read is in flight (`shown`).
 */
function useView<T>(
  enabled: boolean,
  load: (signal: AbortSignal) => Promise<T>,
  nonce: number,
): { readonly state: View<T>; readonly shown: T | null } {
  const [state, setState] = useState<View<T>>(enabled ? { kind: 'loading' } : { kind: 'not_asked' });
  const [shown, setShown] = useState<T | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!enabled) {
      setState({ kind: 'not_asked' });
      return undefined;
    }
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setState({ kind: 'loading' });
    void (async () => {
      try {
        const response = await load(controller.signal);
        if (controller.signal.aborted) return;
        setState({ kind: 'ready', response });
        setShown(response);
      } catch (error) {
        if (controller.signal.aborted) return;
        setState(toProblem(error));
        setShown(null);
      }
    })();
    return () => {
      controller.abort();
    };
    // `load` is a module-level wrapper; the nonce is what re-runs it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, nonce]);

  return { state, shown };
}

export function VerificationPolicy() {
  const surface = requireSurface('verification-policy');
  const { identity } = useIdentity();
  const askAdmin = mayAttempt(identity, POLICY_ADMIN_ROLES);
  const askLegal = mayAttempt(identity, POLICY_LEGAL_ROLES);
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  const admin = useView<VerificationPolicyResponse>(askAdmin, fetchVerificationPolicy, nonce);
  const legal = useView<VerificationSettingsResponse>(askLegal, fetchVerificationSettings, nonce);

  const adminResponse = admin.shown;
  const legalResponse = legal.shown;
  const loading = (admin.state.kind === 'loading' && adminResponse === null) || (legal.state.kind === 'loading' && legalResponse === null);
  const refused = (view: View<unknown>) => view.kind === 'not_asked' || (view.kind === 'problem' && view.problem === 'forbidden');
  const neither = refused(admin.state) && refused(legal.state);
  /* A failure other than the expected 403 is reported, whichever read it was, naming that read's path. */
  const failure =
    admin.state.kind === 'problem' && admin.state.problem !== 'forbidden'
      ? { state: admin.state, path: POLICY_PATH }
      : legal.state.kind === 'problem' && legal.state.problem !== 'forbidden'
        ? { state: legal.state, path: SETTINGS_PATH }
        : null;

  /* Both reads carry the same settings card; Legal's adds caller_is_proposer, so it wins when both answered. */
  const card = legalResponse ?? adminResponse?.settings ?? null;
  const generatedUtc = legalResponse?.generated_utc ?? adminResponse?.generated_utc ?? null;
  /* CLM-127, D-127-1 — read from either response's context (never a database row); both carry the
   * same value, so whichever answered is enough. */
  const selfCertifyEnabled = adminResponse?.self_certify_enabled ?? legalResponse?.self_certify_enabled ?? false;
  const legalView: LegalView | null =
    legalResponse === null
      ? null
      : {
          caller_is_proposer: legalResponse.caller_is_proposer,
          impact: legalResponse.impact,
          pending_opt_ins: legalResponse.pending_opt_ins,
        };

  return (
    <>
      <div className="hd">
        <div>
          <h2 id="verification-policy-heading">{surface.label}</h2>
          <p>{surface.summary}</p>
          <div className="refs">
            {surface.requirements.map((requirement) => (
              <span key={requirement}>{requirement}</span>
            ))}
          </div>
        </div>
      </div>

      <section className="qpanel" aria-labelledby="verification-policy-heading" aria-busy={loading}>
        {loading ? (
          <p role="status" aria-busy="true">
            Reading {PANEL}…
          </p>
        ) : null}

        {!loading && neither ? (
          admin.state.kind === 'problem' ? (
            <PanelProblem
              panel={PANEL}
              state={admin.state}
              endpointPath={POLICY_PATH}
              onRetry={reload}
              forbiddenCopy={VERIFICATION_POLICY_FORBIDDEN_COPY}
            />
          ) : legal.state.kind === 'problem' ? (
            <PanelProblem
              panel={PANEL}
              state={legal.state}
              endpointPath={SETTINGS_PATH}
              onRetry={reload}
              forbiddenCopy={VERIFICATION_POLICY_FORBIDDEN_COPY}
            />
          ) : (
            <p className="banner out" role="status">
              {VERIFICATION_POLICY_FORBIDDEN_COPY}
            </p>
          )
        ) : null}

        {failure === null ? null : (
          <PanelProblem panel={PANEL} state={failure.state} endpointPath={failure.path} onRetry={reload} />
        )}

        {card === null || generatedUtc === null ? null : (
          <SettingsCard
            card={card}
            generatedUtc={generatedUtc}
            isAdmin={adminResponse !== null}
            legal={legalView}
            selfCertifyEnabled={selfCertifyEnabled}
            onChanged={reload}
          />
        )}

        {/* CLM-121 — the contract type's switch: its own read, Admin only; a 403 renders nothing. */}
        {askAdmin && !loading ? <ContractTypeAutoApprovalCard /> : null}

        {adminResponse === null ? null : adminResponse.fields === null ? (
          <p className="banner up" role="status" data-testid="vp-fields-withheld">
            {SCOPE_ALL_REQUIRED_COPY}{' '}
            <Link to={requireSurface('field-accuracy').path}>Open Extraction accuracy</Link>
          </p>
        ) : (
          <FieldPolicyTable
            fields={adminResponse.fields}
            minimum={adminResponse.minimum_sample}
            selfCertifyEnabled={selfCertifyEnabled}
            onChanged={reload}
          />
        )}

        {generatedUtc === null ? null : (
          <p className="mut">
            Generated <time dateTime={generatedUtc}>{generatedUtc}</time>. Each auto-approved value is settled by policy
            and recorded as such — never as verified by a person (CR-06).
          </p>
        )}
      </section>
    </>
  );
}
